import { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Upload, Plus, Monitor, Smartphone, Undo2, Library, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { api, img, errMsg } from "../../lib/api";
import { HeroOrbit } from "../../components/HeroOrbit";
import { adminField, AdminLabel } from "./AdminPages";
import { useUpload } from "./ProductEditor";
import { useHeroDrag } from "../../components/admin/useHeroDrag";
import { ImageStudio } from "../../components/admin/imageStudio/ImageStudio";
import { LibraryPicker } from "../../components/admin/imageStudio/Library";

import { tx, useLang } from "../../lib/i18n";
import { NumInput } from "../../components/NumInput";
const NUM = [["x", "X position %", -10, 100, 0.5], ["y", "Y position %", -10, 100, 0.5], ["width", "Width %", 3, 60, 0.5], ["depth", "Depth (z)", 1, 9, 1],
  ["duration", "Loop seconds", 4, 30, 0.5], ["delay", "Phase offset s", 0, 20, 0.1], ["amplitude", "Bob px", 0, 40, 1], ["tilt", "Tilt °", -10, 10, 0.5]];
const FIELDS = ["name", "image", "product_slug", "x", "y", "width", "depth", "duration", "delay", "amplitude", "tilt", "enabled"];
const sig = (o) => (o ? JSON.stringify(FIELDS.map((k) => o[k] ?? null)) : "");

const ObjectRow = ({ o, i, selected, dirty, onSelect, onChange, onSave, onDelete, onMove, onUpload, onLibrary, onReset }) => { useLang();
  const ref = useRef();
  useEffect(() => { if (selected) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [selected]);
  return (
  <div ref={ref} onClick={onSelect} className={`rounded-2xl border bg-[var(--mv-card)] p-4 ${selected ? "border-[var(--mv-teal)]" : "border-[var(--mv-border)]"}`} data-testid={`hero-admin-object-${i}`}>
    <div className="flex items-center gap-3">
      <img src={img(o.image)} alt="" className="h-14 w-14 rounded-lg mv-card-bg object-contain" />
      <input className={`${adminField} flex-1`} value={o.name} onChange={(e) => onChange({ ...o, name: e.target.value })} />
      <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={o.enabled} onChange={(e) => onChange({ ...o, enabled: e.target.checked })} data-testid={`hero-admin-enabled-${i}`} /> {tx("On")}</label>
      <button onClick={() => onMove(-1)} aria-label={tx("Move up")} data-testid={`hero-admin-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
      <button onClick={() => onMove(1)} aria-label={tx("Move down")}><ArrowDown className="h-4 w-4" /></button>
      <button onClick={onLibrary} aria-label={tx("Select from Image Library")} title={tx("Select from Image Library")} data-testid={`hero-admin-library-${i}`}><Library className="h-4 w-4 text-[var(--mv-teal)]" /></button>
      <button onClick={onUpload} aria-label={tx("Replace image")} data-testid={`hero-admin-replace-${i}`}><Upload className="h-4 w-4 text-[var(--mv-teal)]" /></button>
      <button onClick={onDelete} aria-label={tx("Remove")} data-testid={`hero-admin-delete-${i}`}><Trash2 className="h-4 w-4 text-red-400" /></button>
    </div>
    <div className="mt-3 grid grid-cols-4 gap-2">
      {NUM.map(([k, l, min, max, step]) => (
        <label key={k}><AdminLabel>{tx(l)}</AdminLabel>
          <NumInput min={min} max={max} step={step} className={adminField} value={o[k]} onChange={(e) => onChange({ ...o, [k]: parseFloat(e.target.value) })} data-testid={`hero-admin-${k}-${i}`} />
        </label>
      ))}
    </div>
    <div className="mt-3 flex items-center gap-2">
      <input className={`${adminField} flex-1`} placeholder={tx("Linked product slug (optional)")} value={o.product_slug || ""} onChange={(e) => onChange({ ...o, product_slug: e.target.value })} />
      <button onClick={onReset} disabled={!dirty} className="flex items-center gap-1 rounded-lg border border-[var(--mv-border-2)] px-3 py-2 text-xs disabled:opacity-40" data-testid={`hero-admin-reset-${i}`}><LocateFixed className="h-3.5 w-3.5" />{tx("Reset position")}</button>
      <button onClick={onSave} className={`mv-btn-primary rounded-lg px-4 py-2 text-xs ${dirty ? "ring-2 ring-amber-400" : ""}`} data-testid={`hero-admin-save-${i}`}>{tx("Save")}</button>
    </div>
    {dirty && <p className="mt-2 text-[11px] text-amber-300" data-testid={`hero-admin-unsaved-${i}`}>{tx("Unsaved changes. The live homepage changes only when you click Save.")}</p>}
  </div>
); };

export default function HeroManager() { useLang();
  const [objs, setObjs] = useState([]);
  const [view, setView] = useState("desktop");
  const [hist, setHist] = useState([]);
  const [picker, setPicker] = useState(null);
  const saved = useRef({});
  const fileRef = useRef();
  const target = useRef(null);
  const { upload, busy } = useUpload();
  const objsRef = useRef(objs); objsRef.current = objs;
  const drag = useHeroDrag(setObjs, () => setHist((h) => [...h.slice(-29), objsRef.current]));
  const load = () => api.get("/admin/hero").then((r) => { saved.current = Object.fromEntries(r.data.map((o) => [o.object_id, o])); setObjs(r.data); setHist([]); });
  useEffect(() => { load(); }, []);

  const change = (n) => { setHist((h) => [...h.slice(-29), objs]); setObjs(objs.map((x) => (x.object_id === n.object_id ? n : x))); };
  const save = async (o) => {
    try { const { data } = await api.put(`/admin/hero/${o.object_id}`, o); saved.current[o.object_id] = data; setObjs((a) => [...a]); toast.success(tx("Saved — live on homepage")); } catch (e) { toast.error(tx(errMsg(e))); }
  };
  const undo = () => { if (!hist.length) return; setObjs(hist[hist.length - 1]); setHist(hist.slice(0, -1)); };
  const reset = (o) => { const s = saved.current[o.object_id]; if (s) change({ ...o, x: s.x, y: s.y, width: s.width }); };
  const del = async (o) => { await api.delete(`/admin/hero/${o.object_id}`); load(); };
  const move = async (i, d) => {
    const a = [...objs]; const j = i + d; if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]];
    const r = (await api.post("/admin/hero/reorder", { ids: a.map((o) => o.object_id) })).data;
    setObjs(a.map((o) => ({ ...o, order: r.find((x) => x.object_id === o.object_id)?.order })));
  };
  const pick = (t) => { target.current = t; fileRef.current?.click(); };
  const onFile = async (e) => {
    const [url] = await upload([...e.target.files]);
    e.target.value = "";
    if (!url) return;
    try {
      if (target.current === "new") {
        await api.post("/admin/hero", { name: "New object", image: url, x: 40, y: 30, width: 14, depth: 3, duration: 10, delay: 0, amplitude: 12, tilt: 3, enabled: true });
      } else {
        const o = objs.find((x) => x.object_id === target.current);
        await api.put(`/admin/hero/${o.object_id}`, { ...o, image: url });
      }
      toast.success(tx("Image uploaded")); load();
    } catch (err) { toast.error(tx(errMsg(err))); }
  };
  const onLibraryPick = async (m) => {
    if (picker === "new") {
      try {
        await api.post("/admin/hero", { name: m.name, image: m.url, x: 40, y: 30, width: 14, depth: 3, duration: 10, delay: 0, amplitude: 12, tilt: 3, enabled: false });
        toast.success(tx("Added as a hidden object. Position it, switch it On and click Save.")); load();
      } catch (err) { toast.error(tx(errMsg(err))); }
    } else {
      const o = objs.find((x) => x.object_id === picker);
      if (o) { change({ ...o, image: m.url }); drag.setSelected(o.object_id); toast(tx("Preview updated. Click Save to publish this image.")); }
    }
  };
  const edit = { ...drag };
  return (
    <div className="grid gap-8 xl:grid-cols-12" data-testid="hero-manager">
      <div className="xl:col-span-7">
        <div className="mb-3 flex items-center justify-between">
          <AdminLabel>{tx("Live preview: drag objects to move, corners to resize")}</AdminLabel>
          <div className="flex gap-1">
            <button onClick={undo} disabled={!hist.length} className="flex items-center gap-1 rounded-lg px-2 py-2 text-xs disabled:opacity-40" data-testid="hero-undo"><Undo2 className="h-4 w-4" />{tx("Undo")}</button>
            <button onClick={() => setView("desktop")} className={`rounded-lg p-2 ${view === "desktop" ? "bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : ""}`} data-testid="hero-preview-desktop"><Monitor className="h-4 w-4" /></button>
            <button onClick={() => setView("mobile")} className={`rounded-lg p-2 ${view === "mobile" ? "bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : ""}`} data-testid="hero-preview-mobile"><Smartphone className="h-4 w-4" /></button>
          </div>
        </div>
        <div className={`mx-auto rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-bg)] p-4 ${view === "mobile" ? "max-w-[390px]" : ""}`} data-testid="hero-preview"
          onPointerDown={(e) => { if (!e.target.closest(".hero-obj")) drag.setSelected(null); }}>
          <HeroOrbit objects={view === "mobile" ? objs.filter((o) => o.enabled).slice(0, 5) : objs} edit={edit} />
        </div>
        <p className="mt-3 text-xs text-[var(--mv-dim)]">{tx("Use transparent PNG/WebP cutouts. Up to 12 objects; phones show the first 5. Objects never block clicks.")}</p>
        <ImageStudio />
      </div>
      <div className="xl:col-span-5 space-y-3">
        <button onClick={() => pick("new")} disabled={busy} className="mv-btn-primary flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm" data-testid="hero-admin-add">
          <Plus className="h-4 w-4" /> {tx(busy ? "Uploading…" : "Upload new floating object")}
        </button>
        <button onClick={() => setPicker("new")} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--mv-teal)] py-2.5 text-sm text-[var(--mv-teal)]" data-testid="hero-admin-add-library">
          <Library className="h-4 w-4" /> {tx("New object from Image Library")}
        </button>
        <input ref={fileRef} type="file" accept="image/png,image/webp,image/avif,image/jpeg" hidden onChange={onFile} data-testid="hero-admin-file-input" />
        {objs.map((o, i) => (
          <ObjectRow key={o.object_id} o={o} i={i} selected={drag.selected === o.object_id} dirty={sig(o) !== sig(saved.current[o.object_id])}
            onSelect={() => drag.setSelected(o.object_id)} onChange={change} onSave={() => save(o)} onDelete={() => del(o)} onMove={(d) => move(i, d)}
            onUpload={() => pick(o.object_id)} onLibrary={() => setPicker(o.object_id)} onReset={() => reset(o)} />
        ))}
      </div>
      <LibraryPicker open={!!picker} setOpen={(v) => !v && setPicker(null)} onPick={onLibraryPick} />
    </div>
  );
}
